import { NotFoundPage } from '@dec/shared';
import { PrintingLayout } from 'app/layouts/printing';
import { ManagementReportModule } from 'app/modules/ManagementReport/managementReport.module';
import { TaxReturnPreview } from 'app/modules/Reports/Preview';
import { RecoverPassword } from 'app/modules/Security/pages/passwordRecovery/recoverPassword.lazy';
import { RecoveryRequest } from 'app/modules/Security/pages/passwordRecovery/recoveryRequest.lazy';
import Redireccionar from 'app/modules/Security/pages/redirect';
import Silent from 'app/modules/Security/pages/silent';
import Env from 'env';
import i18next from 'i18next';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import { GlobalStyle } from 'styles/global-styles';
import { QueryParamProvider } from 'use-query-params';
import { ReactRouter6Adapter } from 'use-query-params/adapters/react-router-6';
import * as yup from 'yup';

import AuthCallback from './core/security/AuthCallback';
import AuthRedirect from './core/security/AuthRedirect';
import { DefaultLayout } from './layouts/default';
import { LoginLayout } from './layouts/login';
import { AdministrationModule } from './modules/Administration/administration.module';
import { CollectionsModule } from './modules/Collections/collections.module';
import { CurrentAccountModule } from './modules/current-account/CurrentAccountModule';
import { DisbursementModule } from './modules/disbursement/DisbursementModule';
import { InboxsModule } from './modules/Inbox/inbox.module';
import { NonComplianceModule } from './modules/NonCompliance/non-compliance.module';
import { OperationsModule } from './modules/Operations/operations.module';
import { ParametersModule } from './modules/Parameters/parameters.module';
import { PaymentModule } from './modules/payments/PaymentModule';
import { PenaltiesModule } from './modules/Penalties/penalties.module';
import { ReportsModule } from './modules/Reports/reports.module';
import { Login } from './modules/Security/pages/login/login.lazy';
import { LoginWith2fa } from './modules/Security/pages/loginWith2fa/loginWith2fa.lazy';
import { SeguridadModule } from './modules/Security/security.module';
import { StatementModule } from './modules/Statements/statement.module';
import { TaxesModule } from './modules/taxes/taxes.module';
import { TaxpayerModule } from './modules/Taxpayer/taxpayer.module';
import { TaxStatementModule } from './modules/TaxStatement/taxStatement.module';

yup.setLocale({
  string: {
    min: i18next.t('common.validations.minStringValue'),
    max: ({ max }) => i18next.t('common.validations.maxStringValue', { max })
  },
  number: {
    min: i18next.t('common.validations.minNumberValue'),
    max: i18next.t('common.validations.maxNumberValue')
  }
});
export function App() {
  const { t } = useTranslation();

  return (
    <BrowserRouter basename={Env.basename}>
      <QueryParamProvider adapter={ReactRouter6Adapter}>
        <Helmet titleTemplate={`%s - ${t('app.name')}`} defaultTitle={t('app.legend')}>
          <meta name="description" content={t('app.legend')} />
        </Helmet>

        <Routes>
          <Route
            path="/login"
            element={
              <LoginLayout>
                <Login />
              </LoginLayout>
            }
          />
          <Route
            path="/loginWith2fa"
            element={
              <LoginLayout>
                <LoginWith2fa />
              </LoginLayout>
            }
          />
          <Route
            path="/silent"
            element={
              <LoginLayout>
                <Silent />
              </LoginLayout>
            }
          />
          <Route
            path="/redirect"
            element={
              <LoginLayout>
                <Redireccionar />
              </LoginLayout>
            }
          />
          <Route
            path="/recovery-request"
            element={
              <LoginLayout>
                <RecoveryRequest redirectKey="appUrl" />
              </LoginLayout>
            }
          />

          {/* procesa la redirección en la negociación de oauth */}
          <Route path="/auth/callback" element={<AuthCallback />} />

          {/* Página para la definicion de una nueva contraseña de usuario
          luego de reiniciar conrtaseña mediante opción en login */}
          <Route
            path="/authentication/restorepassword"
            element={
              <LoginLayout>
                <RecoverPassword />
              </LoginLayout>
            }
          />
          <Route
            path="/authentication/changepassword"
            element={
              <LoginLayout>
                <RecoverPassword changePassword={true} />
              </LoginLayout>
            }
          />
          <Route
            path="/authentication/activate"
            element={
              <LoginLayout>
                <RecoverPassword activation={true} />
              </LoginLayout>
            }
          />

          <Route
            path="/reports/taxreturn/preview/:taxReturnId"
            element={
              <AuthRedirect>
                <PrintingLayout>
                  <TaxReturnPreview />
                </PrintingLayout>
              </AuthRedirect>
            }
          />

          <Route
            path="/administration/*"
            element={
              <AuthRedirect>
                <DefaultLayout>
                  <AdministrationModule />
                </DefaultLayout>
              </AuthRedirect>
            }
          />
          <Route
            path="/parameters/*"
            element={
              <AuthRedirect>
                <DefaultLayout>
                  <ParametersModule />
                </DefaultLayout>
              </AuthRedirect>
            }
          />
          <Route
            path="/security/*"
            element={
              <AuthRedirect>
                <DefaultLayout>
                  <SeguridadModule />
                </DefaultLayout>
              </AuthRedirect>
            }
          />
          <Route
            path="/operations/*"
            element={
              <AuthRedirect>
                <DefaultLayout>
                  <OperationsModule />
                </DefaultLayout>
              </AuthRedirect>
            }
          />
          <Route
            path="/taxpayer/*"
            element={
              <AuthRedirect>
                <DefaultLayout>
                  <TaxpayerModule />
                </DefaultLayout>
              </AuthRedirect>
            }
          />
          <Route
            path="/tax-statement/*"
            element={
              <AuthRedirect>
                <DefaultLayout>
                  <TaxStatementModule />
                </DefaultLayout>
              </AuthRedirect>
            }
          />
          <Route
            path="/inbox/*"
            element={
              <AuthRedirect>
                <DefaultLayout>
                  <InboxsModule />
                </DefaultLayout>
              </AuthRedirect>
            }
          />
          <Route
            path="/"
            element={
              <AuthRedirect>
                <DefaultLayout>
                  <ParametersModule />
                </DefaultLayout>
              </AuthRedirect>
            }
          />
          <Route
            path="/statements/*"
            element={
              <AuthRedirect>
                <DefaultLayout>
                  <StatementModule />
                </DefaultLayout>
              </AuthRedirect>
            }
          />
          <Route
            path="/reports/*"
            element={
              <AuthRedirect>
                <DefaultLayout>
                  <ReportsModule />
                </DefaultLayout>
              </AuthRedirect>
            }
          />
          <Route
            path="/management-report/*"
            element={
              <AuthRedirect>
                <DefaultLayout>
                  <ManagementReportModule />
                </DefaultLayout>
              </AuthRedirect>
            }
          />
          <Route
            path="/taxes/*"
            element={
              <AuthRedirect>
                <DefaultLayout>
                  <TaxesModule />
                </DefaultLayout>
              </AuthRedirect>
            }
          />
          <Route
            path="/collections/*"
            element={
              <AuthRedirect>
                <DefaultLayout>
                  <CollectionsModule />
                </DefaultLayout>
              </AuthRedirect>
            }
          />
          <Route
            path="/non-compliances/*"
            element={
              <AuthRedirect>
                <DefaultLayout>
                  <NonComplianceModule />
                </DefaultLayout>
              </AuthRedirect>
            }
          />
          <Route
            path="/penalties/*"
            element={
              <AuthRedirect>
                <DefaultLayout>
                  <PenaltiesModule />
                </DefaultLayout>
              </AuthRedirect>
            }
          />
          <Route
            path="/current-account/*"
            element={
              <AuthRedirect>
                <DefaultLayout>
                  <CurrentAccountModule />
                </DefaultLayout>
              </AuthRedirect>
            }
          />
          <Route
            path="/disbursement/*"
            element={
              <AuthRedirect>
                <DefaultLayout>
                  <DisbursementModule />
                </DefaultLayout>
              </AuthRedirect>
            }
          />
          <Route
            path="/payments/*"
            element={
              <AuthRedirect>
                <DefaultLayout>
                  <PaymentModule />
                </DefaultLayout>
              </AuthRedirect>
            }
          />
          <Route
            path="/"
            element={
              <AuthRedirect>
                <DefaultLayout>
                  <div>
                    <h1>Home - Test</h1>
                  </div>
                </DefaultLayout>
              </AuthRedirect>
            }
          />

          <Route path="*" element={<NotFoundPage />} />
        </Routes>
        <GlobalStyle />
      </QueryParamProvider>
    </BrowserRouter>
  );
}
